/**
 * TanStack Query hooks for Policy Import operations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient, isApiError } from '@/lib/api-client'
import { policyKeys } from '@/hooks/use-policies'
import type {
  ImportPreview,
  ImportExecuteResult,
  PolicyExportBundle,
} from '@/types/policy-import-export'

/**
 * Upload a policy bundle file and get an import preview
 */
export function usePreviewImport() {
  return useMutation({
    mutationFn: async (file: File): Promise<ImportPreview> => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post(
        '/policy-drafts/import/preview',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )

      return response.data.data as ImportPreview
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to preview import file'
      toast.error(message)
    },
  })
}

interface ExecuteImportParams {
  bundle: PolicyExportBundle
  skipConflicts: boolean
}

/**
 * Execute a policy import with conflict resolution
 */
export function useExecuteImport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: ExecuteImportParams): Promise<ImportExecuteResult> => {
      const response = await apiClient.post(
        '/policy-drafts/import/execute',
        params
      )

      return response.data.data as ImportExecuteResult
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: policyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ['attributes'] })

      if (data.policiesCreated > 0) {
        toast.success(`Imported ${data.policiesCreated} policy(s) successfully`)
      }
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to execute import'
      toast.error(message)
    },
  })
}
